import React, { useRef } from "react";
import { useSelector } from "react-redux";

import { DropIndicator } from "app/components/DropIndicator";
import { BoardViewType } from "app/types";
import AddTicket from "../../../../components/AddTicket";

import { shouldShowAddTaskIcon } from "../../../../helpers";
import AddTaskButton from "../../../Home/components/ColumnComponents/AddTaskButton";
import DraggableColumn from "../../../Home/components/ColumnComponents/DraggableColumn";
import { selectShowCreateTicketIntoGroup } from "../../../Tickets/redux/selector";
import { ITickets, TicketGroup } from "../../../Tickets/types";
import { useColumnDrop } from "../../../../components/ticketDragHook";
import clsx from "clsx";

interface SubgroupCellProps {
  group: TicketGroup;
  matchedSub: TicketGroup;
  subGroupId: string | string[];
}

function DropGap({ height }: { height: number }) {
  return (
    <div
      className="w-full rounded-lg border-2 border-dashed border-[#E9ECF2] flex-shrink-0"
      style={{ height: height > 0 ? `${height}px` : "80px" }}
    />
  );
}
export function SubgroupCell({
  group,
  matchedSub,
  subGroupId,
}: SubgroupCellProps) {
  const normalizedSubGroupId = Array.isArray(subGroupId)
    ? subGroupId.join(",")
    : subGroupId;
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const showAddTaskIcon = useSelector(selectShowCreateTicketIntoGroup);
  const isAddTaskVisible = shouldShowAddTaskIcon(
    showAddTaskIcon,
    group._id,
    normalizedSubGroupId,
  );

  const { isDraggingOver, dropEdge, draggingCardHeight, closestTicketId } =
    useColumnDrop({
      group,
      dropzoneRef,
      subGroupId: normalizedSubGroupId,
      ticketsLength: matchedSub?.ticketsList?.length ?? 0,
    });

  return (
    <div className="w-[255px] flex-shrink-0 flex flex-col gap-3 min-h-12">
      {normalizedSubGroupId === showAddTaskIcon.subGroupId &&
        group._id === showAddTaskIcon.groupId && (
          <AddTicket type={BoardViewType.KANBAN} group={group} />
        )}

      <div
        ref={dropzoneRef}
        className={clsx(
          "flex flex-col gap-3 flex-1 relative overflow-y-auto cursor-pointer min-h-[48px] rounded-[8px]  transition-all duration-150",
          isDraggingOver && (matchedSub?.ticketsList?.length ?? 0) > 0
            ? "border-2 border-dashed border-[#E9ECF2]"
            : "transparent",
        )}
      >
        {isDraggingOver && (matchedSub?.ticketsList?.length ?? 0) === 0 && (
          <DropGap height={draggingCardHeight} />
        )}

        {matchedSub?.ticketsList?.map((ticket: ITickets) => {
          const isClosest = ticket._id === closestTicketId;

          return (
            <React.Fragment key={ticket._id}>
              {isClosest && isDraggingOver && dropEdge === "top" && (
                <DropGap height={draggingCardHeight} />
              )}

              <DraggableColumn
                key={ticket._id}
                ticket={ticket}
                group={matchedSub}
                subGroupId={normalizedSubGroupId}
                parentGroupId={group._id}
              />

              {isClosest && isDraggingOver && dropEdge === "bottom" && (
                <DropGap height={draggingCardHeight} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Add Task Button */}
      <div className="  bg-gray-400 w-full">
        <AddTaskButton
          groupId={group._id}
          dropzoneRef={dropzoneRef}
          showAddTaskIcon={isAddTaskVisible}
          subGroupId={normalizedSubGroupId}
        />
      </div>
    </div>
  );
}
